import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { UnitsGridColumn } from 'src/app/shared/components/units-grid-input/units-grid-input.models';
import { TemperaturePoint } from '../../models/temperature-profile.model';
import { WellType } from 'src/app/shared/models/wellType.model';

interface CustomTempProfileStringContext {
  readonly shoeMd: number;
}

interface CustomTempProfileTvdPoint {
  readonly trueVerticalDepth: number;
  readonly temperature: number;
}

export function createCustomTempProfileColumns(): readonly UnitsGridColumn[] {
  return [
    {
      kind: 'number',
      field: 'measuredDepth',
      header: 'MD',
      unitKey: 'longLengths',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 4
    },
    {
      kind: 'number',
      field: 'temperature',
      header: 'Temperature',
      unitKey: 'temperature',
      minFractionDigits: 2,
      maxFractionDigits: 6,
      formatFractionDigits: 4
    }
  ];
}

export function createCustomTempProfileRow(data?: Partial<TemperaturePoint>): FormGroup {
  return new FormGroup({
    measuredDepth: new FormControl<number | null>(data?.measuredDepth ?? null, [Validators.required]),
    temperature: new FormControl<number | null>(data?.temperature ?? null, [Validators.required])
  });
}

export function isCustomTempProfileRowEmpty(row: FormGroup): boolean {
  const md = row.get('measuredDepth')?.value;
  const temperature = row.get('temperature')?.value;
  const isMdEmpty = md === null || md === undefined || md === '' || Number.isNaN(Number(md));
  const isTemperatureEmpty = temperature === null || temperature === undefined || temperature === '' || Number.isNaN(Number(temperature));
  return isMdEmpty && isTemperatureEmpty;
}

// Keeps a single trailing blank row so users can add rows by typing, without needing the context menu.
export function ensureTrailingInputRow(formArray: FormArray<FormGroup>): void {
  if (formArray.length === 0) {
    formArray.push(createCustomTempProfileRow());
    return;
  }

  while (
    formArray.length > 1 &&
    isCustomTempProfileRowEmpty(formArray.at(formArray.length - 1)) &&
    isCustomTempProfileRowEmpty(formArray.at(formArray.length - 2))
  ) {
    formArray.removeAt(formArray.length - 1);
  }

  if (!isCustomTempProfileRowEmpty(formArray.at(formArray.length - 1))) {
    formArray.push(createCustomTempProfileRow());
  }
}

export function populateCustomTempProfileFormArray(
  formArray: FormArray<FormGroup>,
  points: readonly TemperaturePoint[]
): void {
  formArray.clear();
  points.forEach((point) => formArray.push(createCustomTempProfileRow(point)));
  ensureTrailingInputRow(formArray);
}

export function extractCustomTempProfileRows(formArray: FormArray<FormGroup>): TemperaturePoint[] {
  return formArray.controls
    .filter((control) => !isCustomTempProfileRowEmpty(control as FormGroup))
    .map((control) => ({
      measuredDepth: Number(control.get('measuredDepth')?.value ?? 0),
      temperature: Number(control.get('temperature')?.value ?? 0)
    }));
}

export function getCustomTempProfileKeyMds(
  wellType: WellType,
  currentString: CustomTempProfileStringContext
): number[] {
  const keyMds = [wellType?.wellheadDepthFromDrillFloor || 0, currentString.shoeMd];

  if (wellType.type !== 'Land' && wellType.mudlineDepth !== keyMds[0]) {
    keyMds.splice(1, 0, wellType.mudlineDepth);
  }

  return keyMds.sort((a, b) => a - b);
}

export function createInitialCustomTemperaturePoints(
  keyMds: readonly number[],
  keyTvds: readonly number[],
  tempPointsTvd: readonly CustomTempProfileTvdPoint[]
): TemperaturePoint[] {
  return keyMds.map((measuredDepth, index) => ({
    measuredDepth,
    temperature: getTemperatureAtTvd(keyTvds[index], tempPointsTvd)
  }));
}

function getTemperatureAtTvd(
  tvd: number,
  tempPointsTvd: readonly CustomTempProfileTvdPoint[]
): number {
  if (tvd <= tempPointsTvd[0].trueVerticalDepth) {
    return tempPointsTvd[0].temperature;
  }

  if (tvd >= tempPointsTvd[tempPointsTvd.length - 1].trueVerticalDepth) {
    return tempPointsTvd[tempPointsTvd.length - 1].temperature;
  }

  for (let i = 1; i < tempPointsTvd.length; i++) {
    const prev = tempPointsTvd[i - 1];
    const next = tempPointsTvd[i];
    if (tvd <= next.trueVerticalDepth) {
      const frac = (tvd - prev.trueVerticalDepth) / (next.trueVerticalDepth - prev.trueVerticalDepth);
      return prev.temperature + frac * (next.temperature - prev.temperature);
    }
  }

  return tempPointsTvd[tempPointsTvd.length - 1].temperature;
}
